// Podcast: Beiträge aus der Mediathek als Folgen veröffentlichen. Der Feed liegt auf der Hörerseite.

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { CopyValue, errorText } from "../components/streaming-ui";
import { Badge, EmptyState, Page, PageHeader } from "../components/ui";
import type { LibraryItem } from "../types";

interface EpisodeRow {
  id: string;
  slug: string;
  title: string;
  published_at: string | null;
  duration_ms: number | null;
  audio_library_id: string | null;
  show_title: string | null;
  audio_title: string | null;
}

interface FeedConfig {
  title: string;
  description: string;
  author: string;
  email: string;
  image_url: string;
  category: string;
  language: string;
}

const inputClass = "input mt-1.5 font-normal";

function slugify(s: string): string {
  return s.toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "folge";
}

function feedUrlFromError(err: unknown): string | null {
  try {
    const body = JSON.parse(String(err instanceof Error ? err.message : err).replace(/^\d{3}\s*/, "")) as { feed_url?: string };
    return body.feed_url ?? null;
  } catch {
    return null;
  }
}

export function PodcastPage(): JSX.Element {
  const [episodes, setEpisodes] = useState<EpisodeRow[]>([]);
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [config, setConfig] = useState<FeedConfig | null>(null);
  const [configured, setConfigured] = useState(false);
  const [feedUrl, setFeedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [eps, lib] = await Promise.all([api.listEpisodes(undefined, false), api.listLibrary({ limit: 200 })]);
      setEpisodes(eps as EpisodeRow[]);
      setLibrary(lib);
    } catch (err) {
      setError(errorText(err));
    }
    try {
      const c = await api.getPodcastConfig();
      setConfig({ title: c.title, description: c.description, author: c.author ?? "", email: c.email ?? "", image_url: c.image_url ?? "", category: c.category, language: c.language });
      setFeedUrl(c.feed_url);
      setConfigured(true);
    } catch (err) {
      setFeedUrl(feedUrlFromError(err));
      setConfig({ title: "", description: "", author: "", email: "", image_url: "", category: "Music", language: "de-de" });
      setConfigured(false);
    }
  }, []);
  useEffect(() => void load(), [load]);

  const act = async (fn: () => Promise<unknown>, success?: string): Promise<void> => {
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice(success);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Programm"
        title="Podcast"
        description={<>
          Sendungen und Beiträge aus der Mediathek als Podcast-Folgen veröffentlichen. Die Hörerseite liefert den Feed aus;
          die Adresse tragt ihr einmal bei Apple Podcasts, Spotify oder in jeder Podcast-App ein.
        </>}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone="success"><span className="tabular-nums">{episodes.filter((e) => e.published_at).length}</span>&nbsp;veröffentlicht</Badge>
            <Badge><span className="tabular-nums">{episodes.filter((e) => !e.published_at).length}</span>&nbsp;Entwürfe</Badge>
          </div>
        }
      />
      <div className="space-y-6">
      {error && <div className="callout callout-danger">{error}</div>}
      {notice && <div className="callout callout-success">{notice}</div>}

      {config && (
        <FeedSettings
          initial={config}
          configured={configured}
          feedUrl={feedUrl}
          onSave={(c) => act(() => api.savePodcastConfig({
            title: c.title, description: c.description, language: c.language, category: c.category,
            author: c.author || undefined, email: c.email || undefined, image_url: c.image_url || undefined,
          }), "Feed-Angaben gespeichert.")}
        />
      )}

      <section className="space-y-3">
        <h2 className="section-label">Folgen</h2>
        <NewEpisode library={library} onCreate={(input, publish) => act(async () => {
          const ep = await api.createEpisode(input);
          if (publish) await api.publishEpisode(ep.id);
        }, publish ? "Folge veröffentlicht." : "Folge als Entwurf angelegt.")} />
        {episodes.length === 0 ? (
          <EmptyState title="Noch keine Folgen." />
        ) : (
          <div className="card divide-y divide-line overflow-hidden">
          {episodes.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-surface-hover">
              <div className="flex min-w-0 flex-1 basis-64 items-center gap-3">
                <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${e.published_at ? "bg-brand-primary/10 text-brand-primary" : "bg-surface-sunken text-muted"}`} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" className="h-5 w-5"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
                </span>
                <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium text-primary">{e.title}</span>
                  {e.published_at ? <Badge tone="success" dot>veröffentlicht</Badge> : <Badge dot>Entwurf</Badge>}
                  {!e.audio_title && <Badge tone="warning">ohne Audio</Badge>}
                </div>
                <div className="truncate text-xs text-muted">
                  {e.audio_title ? `Audio: ${e.audio_title}` : "ohne Audio – erscheint nicht im Feed"}
                  {e.duration_ms ? ` · ${Math.round(e.duration_ms / 60000)} Min.` : ""}
                  {e.published_at ? ` · veröffentlicht ${new Date(e.published_at).toLocaleDateString("de-DE")}` : " · Entwurf"}
                </div>
                </div>
              </div>
              <div className="flex gap-2">
                {e.published_at ? (
                  <button onClick={() => act(() => api.unpublishEpisode(e.id))} className="btn btn-secondary btn-sm">Zurückziehen</button>
                ) : (
                  <button onClick={() => act(() => api.publishEpisode(e.id))} className="btn btn-primary btn-sm">Veröffentlichen</button>
                )}
                <button onClick={() => window.confirm(`Folge „${e.title}" löschen? Das Audio bleibt in der Mediathek.`) && act(() => api.deleteEpisode(e.id))}
                  className="btn btn-ghost btn-sm text-red-600 dark:text-red-400">Löschen</button>
              </div>
            </div>
          ))}
          </div>
        )}
      </section>
      </div>
    </Page>
  );
}

function FeedSettings({ initial, configured, feedUrl, onSave }: {
  initial: FeedConfig; configured: boolean; feedUrl: string | null; onSave: (c: FeedConfig) => Promise<void>;
}): JSX.Element {
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState(!configured);
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    await onSave(form);
    setOpen(false);
  };
  const field = (key: keyof FeedConfig, label: string, props: Record<string, unknown> = {}): JSX.Element => (
    <label className="block text-[0.8rem] font-medium text-secondary">
      {label}
      <input className={inputClass} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...props} />
    </label>
  );
  return (
    <section className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><h2 className="card-title">Feed</h2>{configured ? <Badge tone="success" dot>eingerichtet</Badge> : <Badge tone="warning" dot>noch nicht eingerichtet</Badge>}</div>
        {configured && <button onClick={() => setOpen((o) => !o)} className="btn btn-secondary btn-sm">{open ? "Schließen" : "Angaben bearbeiten"}</button>}
      </div>
      {configured && feedUrl ? (
        <div className="mt-3"><CopyValue label="Feed" value={feedUrl} /></div>
      ) : (
        <p className="mt-1 text-sm text-muted">Einmal Titel und Beschreibung eintragen, dann gibt es die Feed-Adresse.</p>
      )}
      {open && (
        <form onSubmit={submit} className="mt-4 grid grid-cols-1 gap-4 border-t border-line pt-4 sm:grid-cols-2">
          {field("title", "Titel des Podcasts", { required: true, minLength: 2 })}
          {field("author", "Autor / Radio")}
          <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
            Beschreibung
            <textarea className={inputClass} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required minLength={10} />
          </label>
          {field("email", "Kontakt-E-Mail", { type: "email" })}
          {field("image_url", "Cover-Bild (Adresse, quadratisch, mind. 1400 px)", { type: "url" })}
          <p className="callout callout-info text-xs sm:col-span-2">Apple Podcasts verlangt Cover und Kontakt-E-Mail; ohne sie nimmt Apple den Feed nicht an.</p>
          <div className="sm:col-span-2">
            <button type="submit" className="btn btn-primary">Speichern</button>
          </div>
        </form>
      )}
    </section>
  );
}

function NewEpisode({ library, onCreate }: {
  library: LibraryItem[];
  onCreate: (input: { slug: string; title: string; audio_library_id: string; duration_ms?: number; description_md?: string }, publish: boolean) => Promise<void>;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Wortbeiträge zuerst: Musik ist selten ein Podcast.
  const candidates = [...library].sort((a, b) => Number(a.category === "music") - Number(b.category === "music"));

  if (!open) {
    return <button onClick={() => setOpen(true)} className="btn btn-primary">+ Neue Folge</button>;
  }
  const item = library.find((i) => i.id === itemId);
  const submit = (publish: boolean) => async (e?: FormEvent): Promise<void> => {
    e?.preventDefault();
    if (!item || !title) return;
    await onCreate({ slug: `${slugify(title)}-${Date.now().toString(36).slice(-4)}`, title, audio_library_id: item.id, duration_ms: item.duration_ms ?? undefined, description_md: description || undefined }, publish);
    setOpen(false);
    setItemId("");
    setTitle("");
    setDescription("");
  };
  return (
    <form onSubmit={submit(true)} className="card grid animate-fade-in grid-cols-1 gap-4 p-5 sm:grid-cols-2">
      <div className="card-title sm:col-span-2">Neue Folge</div>
      <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
        Audio aus der Mediathek
        <select className={inputClass} value={itemId} required onChange={(e) => {
          setItemId(e.target.value);
          const it = library.find((i) => i.id === e.target.value);
          if (it && !title) setTitle(it.title);
        }}>
          <option value="">Bitte wählen …</option>
          {candidates.map((i) => (
            <option key={i.id} value={i.id}>{i.title}{i.artist ? ` – ${i.artist}` : ""}{i.duration_ms ? ` (${Math.round(i.duration_ms / 60000)} Min.)` : ""}</option>
          ))}
        </select>
        <span className="field-hint block font-normal">Die Sendung zuerst in der Mediathek hochladen, z. B. als „Wortbeitrag“.</span>
      </label>
      <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
        Titel der Folge
        <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2} />
      </label>
      <label className="block text-[0.8rem] font-medium text-secondary sm:col-span-2">
        Beschreibung (optional)
        <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="flex flex-wrap gap-2 border-t border-line pt-4 sm:col-span-2">
        <button type="submit" className="btn btn-primary">Veröffentlichen</button>
        <button type="button" onClick={() => void submit(false)()} className="btn btn-secondary">Als Entwurf</button>
        <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost">Abbrechen</button>
      </div>
    </form>
  );
}
